import './polyfills' // Must be first: restores Promise.allSettled over bluebird.
import Bowser from 'bowser'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { HashRouter, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { ReactComponent as Icons } from './assets/icons-bundle.svg?react'
import App from './components/App'
import UnsupportedBrowser from './components/UnsupportedBrowser'
import { isProduction } from './utils/env'
import './index.scss'
import ErrorPage, { CriticalErrorPage } from './pages/ErrorPage/ErrorPage'
import Routes from './Routes'
import Provider from './store'

const BrowserCheck = ({ children, component: Component }) => {
  const browser = Bowser.getParser(window.navigator.userAgent)
  const unsupportedBrowsers = ['Internet Explorer']

  const location = useLocation()
  if (unsupportedBrowsers.includes(browser.getBrowserName()) && location.pathname !== '/download') {
    document.body.style.display = 'block'

    return <UnsupportedBrowser />
  }
  return children
}

const waitForFonts = async () => {
  if (!document.fonts?.load) return

  try {
    await Promise.all([
      document.fonts.load('400 16px KoneFont'),
      document.fonts.load('400 16px DroidSansFallback'),
    ])
  } catch (err) {
    console.warn('Failed to preload app fonts', err)
  }
}

if (isProduction) {
  const silencedMethods = ['log', 'group', 'groupCollapsed', 'groupEnd', 'warn', 'error', 'trace']

  // Preserve the original implementations so logging can be re-enabled at runtime.
  const savedConsole = silencedMethods.reduce((saved, method) => {
    saved[method] = console[method]
    return saved
  }, {})

  // Note: `console` is a read-only global under ESM (Vite), so we mutate its
  // methods in place instead of reassigning the binding (the old CRA/webpack way).
  silencedMethods.forEach((method) => {
    console[method] = () => {}
  })

  window.enableLogs = () => {
    silencedMethods.forEach((method) => {
      console[method] = savedConsole[method]
    })
  }
}

const root = createRoot(document.getElementById('root'))

waitForFonts().finally(() => {
  root.render(
    <HelmetProvider>
      <HashRouter>
        <CriticalErrorPage>
          <Provider>
            <BrowserCheck>
              <ErrorPage>
                <App>
                  <Routes />
                </App>
              </ErrorPage>
            </BrowserCheck>
          </Provider>
        </CriticalErrorPage>
        {/* Cache icons here ... use later (see Icon component) */}
        <Icons style={{ display: 'none' }} />
      </HashRouter>
    </HelmetProvider>
  )
})
